<template>
  <div>
    <!-- Sub-layer checkboxes (e.g. market/submarket toggle) -->
    <div
      v-if="subLayerToggleEnabled"
      class="sub-layer-toggles"
    >
      <v-checkbox-btn
        v-for="subLayer in subLayers"
        :key="subLayer.id"
        :model-value="subLayer.checked !== false"
        :label="subLayer.label || subLayer.id"
        density="compact"
        :ripple="false"
        @update:model-value="toggleSubLayer(subLayer.id)"
      />
    </div>

    <!-- Legend groups -->
    <div v-if="displayLegendGroups.length > 0">
      <div
        v-for="(legendGroup, index) in displayLegendGroups"
        :key="`${legendGroup.label}-${index}`"
      >
        <!-- There is an assumption that if a group only has one item, the item label matches the label of the  -->
        <!-- group, thereby making the group label redundant. We may want to revisit this in the future. -->
        <LegendGroup
          :no-label="!alwaysShowLegendLabel
            && (legendGroup?.legendSymbols?.length === 1 || displayLegendGroups.length === 1)"
          :legend-group="legendGroup"
          :disable-toggle="subLayerToggleEnabled"
          @toggle-group-visibility="toggleGroupVisibility"
          @toggle-item-visibility="toggleItemVisibility"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {LegendGroup as LegendGroupClass} from '@/lib/classes/Legend/LegendGroup';
import type {LegendGroupConfig} from '@/services/GraphicService/MapLayer';
import LegendGroup from './LegendGroup.vue';
import {mapActions} from 'vuex';

export default {
  name: 'VerticalLegend',
  components: {
    LegendGroup,
  },
  props: {
    layer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      legendGroups: this.layer.activeLegendGroups || [],
      visible: true,
    };
  },
  computed: {
    subLayers() {
      return this.layer.displaySubLayers || [];
    },
    subLayerToggleEnabled(): boolean {
      return this.layer.subLayerToggleEnabled ?? false;
    },
    alwaysShowLegendLabel(): boolean {
      return this.layer.alwaysShowLegendLabel ?? false;
    },
    displayLegendGroups() {
      if (!this.subLayerToggleEnabled) {
        return this.legendGroups;
      }
      // When multiple sub-layers exist, the legend symbols are the same across sub-layers.
      // Show groups from only the first sub-layer to avoid duplicates.
      const firstSubLayer = this.subLayers[0];
      if (!firstSubLayer?.legendGroups?.length) return [];
      return firstSubLayer.legendGroups
        .map((groupConfig: LegendGroupConfig) =>
          this.layer.legendGroups?.find((g: LegendGroupClass) => g.label === groupConfig.label))
        .filter(Boolean);
    },
  },
  watch: {
    'layer': {
      handler() {
        this.legendGroups = this.layer.activeLegendGroups || [];
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('layers', ['updateUserLayer']),
    toggleSubLayer(subLayerId: string) {
      this.layer.toggleSubLayer(subLayerId);
      this.updateUserLayer({
        id: this.layer.id,
        options: this.layer.options,
      });
    },
    toggleItemVisibility(groupId: string, itemId: string, visible: boolean) {
      const group = this.legendGroups.find((g: LegendGroupClass) => g.uid === groupId);
      if (!group) {
        console.warn(`Group with id ${groupId} not found in legend groups.`);
        return;
      }
      group.toggleItemVisibility(itemId, visible);
      // Update the filters of the layer based on the visibility changes
      this.layer.updateLegendFiltering(true);
      this.updateUserLayer({
        id: this.layer.id,
        options: this.layer.options,
      });
      return;
    },
    toggleGroupVisibility(groupId: string, visible: boolean) {
      const group = this.legendGroups.find((g: LegendGroupClass) => g.uid === groupId);
      if (!group) {
        console.warn(`Group with id ${groupId} not found in legend groups.`);
        return;
      }
      group.setVisibilityForAll(visible);
      // Update the filters of the layer based on the visibility changes
      this.layer.updateLegendFiltering(true);
      this.updateUserLayer({
        id: this.layer.id,
        options: this.layer.options,
      });
      return;
    },
  },
};
</script>

<style lang="scss" scoped>
.sub-layer-toggles {
  padding-bottom: 0.5rem;

  :deep(.v-checkbox-btn) {
    min-height: unset;

    .v-selection-control__wrapper {
      height: 18px;
      width: 18px;
      margin-right: 0.5rem;

      .v-icon {
        color: main.$npGray;
      }
    }

    .v-label {
      font-family: main.$fontPrimary;
      color: #666;
      font-weight: 500;
      padding-inline-start: 0;
    }
  }
}
</style>
